import { useEffect, useRef } from "react";
import { AlertTriangle, Trash2, X } from "lucide-react";
import { useAdminT } from "../../../../hooks/useAdminT";

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
    variant?: "danger" | "warning";
    isLoading?: boolean;
}

export function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    variant = "danger",
    isLoading = false,
}: ConfirmDialogProps) {
    const { t } = useAdminT();
    const confirmRef = useRef<HTMLButtonElement>(null);

    // Defaults come from translations
    const resolvedTitle = title ?? t("common.confirmAction");
    const resolvedMessage = message ?? t("common.confirmProceed");
    const resolvedConfirmText = confirmText ?? t("common.confirm");
    const resolvedCancelText = cancelText ?? t("common.cancel");

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => confirmRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) onClose();
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isDanger = variant === "danger";

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-start gap-4 p-6 pb-2">
                    <div className={`p-2.5 rounded-full ${isDanger ? "bg-red-100" : "bg-amber-100"}`}>
                        {isDanger ? (
                            <Trash2 className="w-5 h-5 text-red-600" />
                        ) : (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-slate-900">{resolvedTitle}</h3>
                        <p className="mt-1 text-sm text-slate-500">{resolvedMessage}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded transition"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 p-6 pt-4">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                    >
                        {resolvedCancelText}
                    </button>
                    <button
                        ref={confirmRef}
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50 ${isDanger
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-amber-600 hover:bg-amber-700"
                            }`}
                    >
                        {isLoading ? t("common.processing") : resolvedConfirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
